import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { profileAPI } from '../services/api';
import { FOCUS } from './ds';
import { cn } from '../lib/utils';
import { BANNER_INSET, PageBanner } from './appstate/PageBanner';

/**
 * Banner shown only to non-paying (lapsed_tier) accounts. There is no free
 * tier: lapsed_tier is an account inside its 7-day trial, after it, or one that
 * never started it.
 * - Trial active → counts down the trial + nudges to keep Pro (Figma "Banner ·
 *   Trial running out", Neutral).
 * - Trial over → the coach is paused until they subscribe (Figma "Banner ·
 *   Coach paused", Warning).
 * - Trial never started → start it or subscribe (Neutral).
 * Pro/Ultimate/admin users see nothing. Fails silent on error.
 */
const ACTION = cn('inline-flex h-10 shrink-0 items-center justify-center rounded-full bg-accent-default px-4 type-button-small text-accent-on transition-colors hover:bg-accent-hover', FOCUS);

const UsageBanner = () => {
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    profileAPI
      .getUsage()
      .then((u) => { if (!cancelled) setUsage(u); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return <UsageBannerView usage={usage} />;
};

export function UsageBannerView({ usage }) {
  // Only lapsed (stored) accounts see the banner — trial users are stored lapsed.
  if (!usage || usage.tier !== 'lapsed_tier') return null;

  // Trial users (stored lapsed, effectively Pro) get a countdown. Anyone else
  // here has zero AI, so there's no budget to show — just the way back.
  const trialActive = usage.trial_active;
  const daysLeft = usage.trial_days_remaining;

  const [tone, message, cta] = trialActive
    ? ['neutral', `${daysLeft} day${daysLeft === 1 ? '' : 's'} left of your Pro trial. Keep your coach adapting your plan.`, 'Keep Pro']
    : usage.trial_ended
      ? ['warning', 'Your coach is paused. Your plan stays, but it won’t adapt until you subscribe.', 'See plans']
      : ['neutral', 'Start your 7-day free trial or subscribe to unlock your AI coach.', 'Subscribe'];

  return (
    <div className={BANNER_INSET}>
      <PageBanner tone={tone} action={<Link to="/settings?tab=plan" className={ACTION}>{cta}</Link>}>
        {message}
      </PageBanner>
    </div>
  );
}

export default UsageBanner;
